import { z } from 'zod';

export const userStatusSchema = z.enum([
  'active',
  'pending_activation',
  'revoked',
]);

export const roleSchema = z.enum(['org_admin', 'member']);
export const workosSyncStateSchema = z.enum([
  'synced',
  'awaiting_sync',
  'drifted',
  'failed',
  'unlinked',
]);

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  domain: z.string(),
  status: z.string(),
});

export const memberSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  role: roleSchema,
  status: userStatusSchema,
  createdAt: z.string(),
  invitationLastSentAt: z.string().nullable().default(null),
  invitationCanResendAt: z.string().nullable().default(null),
  syncState: workosSyncStateSchema.default('unlinked'),
  observedStatus: z.enum(['active', 'pending', 'inactive', 'missing']).nullable().default(null),
  observedRole: z.string().nullable().default(null),
  observedAt: z.string().nullable().default(null),
  invitationState: z.enum(['pending', 'accepted', 'expired', 'revoked']).nullable().default(null),
  invitationExpiresAt: z.string().nullable().default(null),
  lastLoginAt: z.string().nullable().default(null),
});

export const platformMemberSchema = memberSchema.extend({
  syncFailureCount: z.number().int().nonnegative().default(0),
  syncErrorCode: z.enum(['unavailable', 'rate_limited', 'configuration', 'conflict', 'unknown']).nullable().default(null),
  syncLastAttemptAt: z.string().nullable().default(null),
  syncLastSuccessAt: z.string().nullable().default(null),
});

export const authenticationSeriesPointSchema = z.object({
  date: z.string(),
  successful: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  risky: z.number().int().nonnegative(),
});

const authenticationSummarySchema = z.object({
  successful: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  risky: z.number().int().nonnegative(),
  successRate: z.number().nullable(),
});

export const customerIdentityHealthSchema = z.object({
  currentUser: z.object({
    activeSessionCount: z.number().int().nonnegative().nullable(),
    lastLoginAt: z.string().nullable(),
    liveStatus: z.enum(['ok', 'unavailable']),
  }),
  series: z.array(authenticationSeriesPointSchema),
  summary: authenticationSummarySchema,
  coverageStart: z.string().nullable(),
});

export const platformIdentityHealthSchema = z.object({
  series: z.array(authenticationSeriesPointSchema),
  summary: authenticationSummarySchema.extend({
    affectedOrganizations: z.number().int().nonnegative(),
  }),
  organizations: z.array(z.object({
    organizationId: z.string(),
    organizationName: z.string(),
    syncState: workosSyncStateSchema,
    memberCount: z.number().int().nonnegative(),
    failedMembers: z.number().int().nonnegative(),
    driftedMembers: z.number().int().nonnegative(),
    awaitingMembers: z.number().int().nonnegative(),
    retryCount: z.number().int().nonnegative(),
    lastCheckedAt: z.string().nullable(),
    errorCode: z.string().nullable(),
    coverageStart: z.string().nullable(),
  })),
});

export const licenseSchema = z.object({
  status: z.string(),
  startDate: z.string().nullable().optional(),
  expiryDate: z.string(),
  seatLimit: z.number().int().nonnegative(),
  usedSeats: z.number().int().nonnegative().optional(),
  limitsSummary: z.string(),
  licensedDomains: z.array(z.string()),
  releaseChannel: z.enum(['stable', 'beta']).default('stable'),
  deploymentModel: z.enum(['saas_hybrid', 'on_prem']).default('on_prem'),
  downloadAvailable: z.boolean().default(false),
});

export const userRequestSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  requestedRole: roleSchema,
  status: z.string(),
  createdAt: z.string(),
});

export const userImportInputSchema = z.object({
  fullName: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(254),
  role: roleSchema,
});

export const userImportRowResultSchema = z.object({
  fullName: z.string(),
  email: z.string(),
  role: roleSchema,
  row: z.number().int().positive(),
  valid: z.boolean(),
  errors: z.array(z.string()),
});

export const userImportValidationSchema = z.object({
  rows: z.array(userImportRowResultSchema),
  ready: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  availableSeats: z.number().int().nonnegative(),
});

export const notificationSchema = z.object({
  id: z.string(),
  type: z.enum(['license', 'release', 'ticket', 'approval', 'system']),
  title: z.string(),
  body: z.string(),
  actionPath: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});

export const softwareReleaseSchema = z.object({
  id: z.string(),
  version: z.string(),
  channel: z.enum(['stable', 'beta']),
  releaseNotes: z.string(),
  deploymentModel: z.enum(['saas_hybrid', 'on_prem']).default('on_prem'),
  buildProfile: z.enum(['workos', 'license']).default('license'),
  sourceCommit: z.string().nullable().default(null),
  sourceRef: z.string().nullable().default(null),
  cloudflareProject: z.enum(['lunex-mypdf', '8m48e-mypdf']).nullable().default(null),
  cloudflareDeploymentId: z.string().nullable().default(null),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().positive(),
  sha256: z.string(),
  status: z.enum(['draft', 'published', 'archived']),
  publishedAt: z.string().nullable(),
  classifiedAt: z.string().nullable().default(null),
  artifactUploadedAt: z.string().nullable().default(null),
  audience: z.enum(['all', 'selected']).default('all'),
  targetCount: z.number().int().nonnegative().default(0),
  downloadUrl: z.string().nullable(),
});

export const ticketAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().positive(),
  kind: z.enum(['image', 'document']),
  url: z.string(),
});

export const ticketSchema = z.object({
  id: z.string(),
  organizationId: z.string().optional(),
  organizationName: z.string().optional(),
  subject: z.string(),
  status: z.enum(['open', 'in_progress', 'closed']),
  priority: z.enum(['low', 'normal', 'high']),
  createdAt: z.string(),
  updatedAt: z.string(),
  messages: z
    .array(
      z.object({
        id: z.string(),
        authorType: z.enum(['customer', 'platform']),
        authorName: z.string(),
        authorEmail: z.string(),
        body: z.string(),
        createdAt: z.string(),
        attachments: z.array(ticketAttachmentSchema).default([]),
      })
    )
    .default([]),
});

export const consoleStateSchema = z.object({
  mode: z.literal('customer').default('customer'),
  organization: organizationSchema,
  currentUser: memberSchema,
  members: z.array(memberSchema),
  userRequests: z.array(userRequestSchema),
  license: licenseSchema.nullable(),
  tickets: z.array(ticketSchema),
  notifications: z.array(notificationSchema).default([]),
  releases: z.array(softwareReleaseSchema).default([]),
});

export const platformDomainSchema = z.object({
  id: z.string(),
  hostname: z.string(),
  environment: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
});

export const platformOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  workosOrganizationId: z.string().nullable(),
  primaryDomain: z.string(),
  domains: z.array(platformDomainSchema),
  license: licenseSchema.nullable(),
  members: z.array(platformMemberSchema),
  tickets: z.array(ticketSchema).default([]),
  userRequests: z.array(userRequestSchema).default([]),
});

export const platformStateSchema = z.object({
  mode: z.literal('platform'),
  currentUser: z.object({
    email: z.string().email(),
    fullName: z.string(),
  }),
  organizations: z.array(platformOrganizationSchema),
  releases: z.array(softwareReleaseSchema).default([]),
});

export const appStateSchema = z.discriminatedUnion('mode', [
  consoleStateSchema,
  platformStateSchema,
]);

export type ConsoleState = z.infer<typeof consoleStateSchema>;
export type PlatformState = z.infer<typeof platformStateSchema>;
export type PlatformOrganization = z.infer<typeof platformOrganizationSchema>;
export type AppState = z.infer<typeof appStateSchema>;
export type Member = z.infer<typeof memberSchema>;
export type CustomerIdentityHealth = z.infer<typeof customerIdentityHealthSchema>;
export type PlatformIdentityHealth = z.infer<typeof platformIdentityHealthSchema>;
export type UserRequest = z.infer<typeof userRequestSchema>;
export type UserImportInput = z.infer<typeof userImportInputSchema>;
export type UserImportCandidate = { fullName: string; email: string; role: string };
export type UserImportValidation = z.infer<typeof userImportValidationSchema>;
export type Ticket = z.infer<typeof ticketSchema>;
export type TicketMessage = Ticket['messages'][number];
export type TicketAttachment = z.infer<typeof ticketAttachmentSchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type SoftwareRelease = z.infer<typeof softwareReleaseSchema>;

export class AuthenticationRequiredError extends Error {
  constructor() {
    super('Authentication required');
    this.name = 'AuthenticationRequiredError';
  }
}

export class ConsoleAccessDeniedError extends Error {
  constructor(message = 'Console access requires an organization admin account.') {
    super(message);
    this.name = 'ConsoleAccessDeniedError';
  }
}

const mockConsoleState: ConsoleState = {
  mode: 'customer',
  organization: {
    id: 'org_local_mock',
    name: 'Local myPDF Customer',
    slug: 'local-mypdf-customer',
    domain: 'localhost',
    status: 'active',
  },
  currentUser: {
    id: 'member_local_admin',
    email: 'admin@example.com',
    fullName: 'Local Admin',
    role: 'org_admin',
    status: 'active',
    createdAt: '2026-06-10T00:00:00.000Z',
    invitationLastSentAt: null,
    invitationCanResendAt: null,
    syncState: 'synced',
    observedStatus: 'active',
    observedRole: 'admin',
    observedAt: '2026-08-20T06:30:00.000Z',
    invitationState: null,
    invitationExpiresAt: null,
    lastLoginAt: '2026-08-20T05:42:00.000Z',
  },
  members: [
    {
      id: 'member_local_admin',
      email: 'admin@example.com',
      fullName: 'Local Admin',
      role: 'org_admin',
      status: 'active',
      createdAt: '2026-06-10T00:00:00.000Z',
      invitationLastSentAt: null,
      invitationCanResendAt: null,
      syncState: 'synced', observedStatus: 'active', observedRole: 'admin', observedAt: '2026-08-20T06:30:00.000Z', invitationState: null, invitationExpiresAt: null, lastLoginAt: '2026-08-20T05:42:00.000Z',
    },
    {
      id: 'member_pending',
      email: 'pending.member@example.com',
      fullName: 'Pending Member',
      role: 'member',
      status: 'pending_activation',
      createdAt: '2026-06-10T00:00:00.000Z',
      invitationLastSentAt: '2026-06-10T00:00:00.000Z',
      invitationCanResendAt: '2026-06-11T00:00:00.000Z',
      syncState: 'synced', observedStatus: 'pending', observedRole: 'member', observedAt: '2026-08-20T06:30:00.000Z', invitationState: 'pending', invitationExpiresAt: '2026-08-27T06:30:00.000Z', lastLoginAt: null,
    },
    {
      id: 'member_revoked',
      email: 'revoked.admin@example.com',
      fullName: 'Revoked Admin',
      role: 'org_admin',
      status: 'revoked',
      createdAt: '2026-06-10T00:00:00.000Z',
      invitationLastSentAt: null,
      invitationCanResendAt: null,
      syncState: 'synced', observedStatus: 'inactive', observedRole: 'admin', observedAt: '2026-08-20T06:30:00.000Z', invitationState: 'revoked', invitationExpiresAt: null, lastLoginAt: '2026-07-30T10:00:00.000Z',
    },
  ],
  userRequests: [
    {
      id: 'request_seat_expansion',
      email: 'expansion.request@example.com',
      fullName: 'Expansion Request',
      requestedRole: 'member',
      status: 'pending_management_approval',
      createdAt: '2026-06-10T00:00:00.000Z',
    },
  ],
  license: {
    status: 'active',
    startDate: '2026-01-01',
    expiryDate: '2030-01-01',
    seatLimit: 500,
    limitsSummary: 'Organization admin console access only. PDF processing remains client-side.',
    licensedDomains: ['localhost'],
    releaseChannel: 'stable',
    deploymentModel: 'on_prem',
    downloadAvailable: true,
  },
  notifications: [
    {
      id: 'notification_ticket_reply',
      type: 'ticket',
      title: 'Support replied to your ticket',
      body: 'We reviewed your onboarding request and added the next steps.',
      actionPath: '/tickets',
      createdAt: '2026-08-09T12:00:00.000Z',
      readAt: null,
    },
  ],
  releases: [],
  tickets: [
    {
      id: 'ticket_onboarding',
      organizationId: 'org_local_mock',
      organizationName: 'Local myPDF Customer',
      subject: 'Initial console onboarding',
      status: 'open',
      priority: 'normal',
      createdAt: '2026-06-10T00:00:00.000Z',
      updatedAt: '2026-06-10T00:00:00.000Z',
      messages: [
        {
          id: 'message_onboarding',
          authorType: 'customer',
          authorName: 'Local Admin',
          authorEmail: 'admin@example.com',
          body: 'Initial console onboarding',
          createdAt: '2026-06-10T00:00:00.000Z',
          attachments: [],
        },
      ],
    },
  ],
};

const mockTicketSubjects = [
  'License renewal question',
  'Unable to invite a member',
  'Domain verification request',
  'Seat count reconciliation',
  'Account access review',
  'Pending activation follow-up',
  'Tools dashboard configuration',
  'Organization profile update',
  'Admin role reassignment',
  'Billing contact correction',
  'SSO readiness check',
  'Production domain migration',
] as const;

function createMockPlatformTickets(
  organizationId: string,
  organizationName: string,
  count: number,
  seededTickets: Ticket[] = []
): Ticket[] {
  const generated = Array.from({ length: Math.max(0, count - seededTickets.length) }, (_, offset) => {
    const index = seededTickets.length + offset;
    const day = String((index % 28) + 1).padStart(2, '0');
    const createdAt = `2026-07-${day}T09:00:00.000Z`;
    const status = (['open', 'in_progress', 'closed'] as const)[index % 3];
    const priority = (['normal', 'high', 'low'] as const)[index % 3];
    const subject = mockTicketSubjects[index % mockTicketSubjects.length];

    return ticketSchema.parse({
      id: `ticket_${organizationId}_${index + 1}`,
      organizationId,
      organizationName,
      subject,
      status,
      priority,
      createdAt,
      updatedAt: createdAt,
      messages: [
        {
          id: `message_${organizationId}_${index + 1}`,
          authorType: 'customer',
          authorName: `${organizationName} Admin`,
          authorEmail: `admin${index + 1}@example.com`,
          body: `${subject}. Please review this local demonstration ticket.`,
          createdAt,
          attachments: [],
        },
      ],
    });
  });

  return [...seededTickets, ...generated];
}

function createMockPlatformOrganization({
  domain,
  id,
  name,
  slug,
  ticketCount,
  tickets = [],
  deploymentModel = 'on_prem',
}: {
  domain: string;
  id: string;
  name: string;
  slug: string;
  ticketCount: number;
  tickets?: Ticket[];
  deploymentModel?: 'saas_hybrid' | 'on_prem';
}): PlatformOrganization {
  return platformOrganizationSchema.parse({
    id,
    name,
    slug,
    status: 'active',
    workosOrganizationId: null,
    primaryDomain: domain,
    domains: [
      {
        id: `domain_${id}`,
        hostname: domain,
        environment: 'local',
        active: true,
        createdAt: '2026-06-10T00:00:00.000Z',
      },
    ],
    license: {
      status: 'active',
      startDate: '2026-01-01',
      expiryDate: '2030-01-01',
      seatLimit: 500,
      usedSeats: id === 'org_local_mock' ? 3 : 0,
      limitsSummary: 'Local platform administration demonstration.',
      licensedDomains: [domain],
      releaseChannel: id === 'org_northstar_mock' ? 'beta' : 'stable',
      deploymentModel,
    },
    members: id === 'org_local_mock' ? mockConsoleState.members : [],
    userRequests: id === 'org_local_mock' ? mockConsoleState.userRequests : [],
    tickets: createMockPlatformTickets(id, name, ticketCount, tickets),
  });
}

let mockPlatformState: PlatformState = platformStateSchema.parse({
  mode: 'platform',
  currentUser: {
    email: 'admin@lunex.bh',
    fullName: 'Local Lunex Admin',
  },
  organizations: [
    createMockPlatformOrganization({
      domain: mockConsoleState.organization.domain,
      id: mockConsoleState.organization.id,
      name: mockConsoleState.organization.name,
      slug: mockConsoleState.organization.slug,
      ticketCount: 14,
      tickets: mockConsoleState.tickets,
    }),
    createMockPlatformOrganization({
      domain: 'northstar.localhost',
      id: 'org_northstar_mock',
      name: 'Northstar Finance',
      slug: 'northstar-finance',
      ticketCount: 12,
    }),
    createMockPlatformOrganization({
      domain: 'harbor.localhost',
      id: 'org_harbor_mock',
      name: 'Harbor Legal',
      slug: 'harbor-legal',
      ticketCount: 11,
      deploymentModel: 'saas_hybrid',
    }),
    createMockPlatformOrganization({
      domain: 'cedar.localhost',
      id: 'org_cedar_mock',
      name: 'Cedar Health',
      slug: 'cedar-health',
      ticketCount: 13,
    }),
  ],
  releases: [
    {
      id: 'release_workos_draft_0201', version: '2.0.1', channel: 'stable', releaseNotes: 'WorkOS customer deployment release.',
      deploymentModel: 'saas_hybrid', buildProfile: 'workos', sourceCommit: 'a5923ec0ab1de75a8e990b2330034528b87d159e', sourceRef: 'refs/heads/main',
      cloudflareProject: 'lunex-mypdf', cloudflareDeploymentId: null,
      fileName: 'mypdf-saas-hybrid-2.0.1-a5923ec0ab1d.zip', contentType: 'application/zip', sizeBytes: 35_651_584,
      sha256: 'e'.repeat(64), status: 'draft', publishedAt: null, classifiedAt: '2026-08-11T12:00:00.000Z',
      artifactUploadedAt: '2026-08-11T11:50:00.000Z', audience: 'selected', targetCount: 0, downloadUrl: null,
    },
    {
      id: 'release_draft_0120', version: '0.12.0', channel: 'stable', releaseNotes: '',
      fileName: 'mypdf-onprem-0.12.0.zip', contentType: 'application/zip', sizeBytes: 48_320_512,
      sha256: 'a'.repeat(64), status: 'draft', publishedAt: null, classifiedAt: null,
      artifactUploadedAt: '2026-08-11T08:30:00.000Z', audience: 'all', targetCount: 0, downloadUrl: null,
    },
    {
      id: 'release_draft_0111', version: '0.11.1', channel: 'beta', releaseNotes: 'Improved domain validation and update checks.',
      fileName: 'mypdf-onprem-0.11.1.zip', contentType: 'application/zip', sizeBytes: 47_810_144,
      sha256: 'b'.repeat(64), status: 'draft', publishedAt: null, classifiedAt: '2026-08-10T14:00:00.000Z',
      artifactUploadedAt: '2026-08-10T13:45:00.000Z', audience: 'selected', targetCount: 0, downloadUrl: null,
    },
    {
      id: 'release_published_0110', version: '0.11.0', channel: 'stable', releaseNotes: 'Production release with access and license improvements.',
      fileName: 'mypdf-onprem-0.11.0.zip', contentType: 'application/zip', sizeBytes: 46_720_000,
      sha256: 'c'.repeat(64), status: 'published', publishedAt: '2026-08-01T10:00:00.000Z', classifiedAt: '2026-08-01T09:00:00.000Z',
      artifactUploadedAt: '2026-08-01T08:50:00.000Z', audience: 'all', targetCount: 4, downloadUrl: '/api/updates/releases/release_published_0110/download',
    },
    {
      id: 'release_published_0105', version: '0.10.5', channel: 'beta', releaseNotes: 'Beta channel validation release.',
      fileName: 'mypdf-onprem-0.10.5.zip', contentType: 'application/zip', sizeBytes: 45_910_000,
      sha256: 'd'.repeat(64), status: 'published', publishedAt: '2026-07-20T10:00:00.000Z', classifiedAt: '2026-07-20T09:00:00.000Z',
      artifactUploadedAt: '2026-07-20T08:50:00.000Z', audience: 'selected', targetCount: 2, downloadUrl: '/api/updates/releases/release_published_0105/download',
    },
  ],
});

export async function fetchConsoleState(): Promise<AppState> {
  if (shouldUseVitePlatformMock()) return mockPlatformState;

  const response = await fetch('/api/console', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthenticationRequiredError();
    }

    if (response.status === 403) {
      const platformResponse = await fetch('/api/platform', {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (platformResponse.ok) {
        return platformStateSchema.parse(await platformResponse.json());
      }

      throw new ConsoleAccessDeniedError(await readErrorMessage(response));
    }

    if (shouldUseLocalMock(response)) {
      return loadLocalMockConsoleState();
    }

    throw new Error(`Console state request failed: ${response.status}`);
  }

  return appStateSchema.parse(await response.json());
}

function createMockAuthenticationSeries() {
  return Array.from({ length: 30 }, (_, offset) => {
    const date = new Date(Date.UTC(2026, 6, 22 + offset)).toISOString().slice(0, 10);
    return {
      date,
      successful: 8 + ((offset * 7) % 13),
      failed: offset % 6 === 0 ? 2 : offset % 3,
      risky: offset % 11 === 0 ? 1 : 0,
    };
  });
}

export async function fetchCustomerIdentityHealth(): Promise<CustomerIdentityHealth> {
  const response = await fetch('/api/identity-health', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const series = createMockAuthenticationSeries();
      const successful = series.reduce((sum, point) => sum + point.successful, 0);
      const failed = series.reduce((sum, point) => sum + point.failed, 0);
      const risky = series.reduce((sum, point) => sum + point.risky, 0);
      return customerIdentityHealthSchema.parse({
        currentUser: { activeSessionCount: null, lastLoginAt: '2026-08-20T05:42:00.000Z', liveStatus: 'unavailable' },
        series,
        summary: { successful, failed, risky, successRate: Math.round((successful / (successful + failed)) * 1000) / 10 },
        coverageStart: series[0]?.date ?? null,
      });
    }
    throw new Error((await readErrorMessage(response)) ?? `Identity health request failed: ${response.status}`);
  }
  return customerIdentityHealthSchema.parse(await response.json());
}

export async function fetchPlatformIdentityHealth(): Promise<PlatformIdentityHealth> {
  const response = await fetch('/api/platform/identity-health', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const series = createMockAuthenticationSeries();
      const successful = series.reduce((sum, point) => sum + point.successful, 0);
      const failed = series.reduce((sum, point) => sum + point.failed, 0);
      const risky = series.reduce((sum, point) => sum + point.risky, 0);
      return platformIdentityHealthSchema.parse({
        series,
        summary: { successful, failed, risky, successRate: 96.4, affectedOrganizations: 2 },
        organizations: mockPlatformState.organizations.map((organization, index) => ({
          organizationId: organization.id,
          organizationName: organization.name,
          syncState: index === 2 ? 'awaiting_sync' : organization.workosOrganizationId ? 'synced' : 'unlinked',
          memberCount: organization.members.length,
          failedMembers: 0,
          driftedMembers: 0,
          awaitingMembers: index === 2 ? 1 : 0,
          retryCount: index === 2 ? 1 : 0,
          lastCheckedAt: '2026-08-20T06:30:00.000Z',
          errorCode: null,
          coverageStart: series[0]?.date ?? null,
        })),
      });
    }
    throw new Error((await readErrorMessage(response)) ?? `Platform identity health request failed: ${response.status}`);
  }
  return platformIdentityHealthSchema.parse(await response.json());
}

async function readErrorMessage(response: Response) {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : undefined;
  } catch {
    return undefined;
  }
}

export async function requestUserAccess(input: {
  email: string;
  fullName: string;
  role: z.infer<typeof roleSchema>;
}): Promise<{ member?: Member; request?: UserRequest; outcome: 'provisioning' | 'approval_required' }> {
  const response = await fetch('/api/users/requests', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const nonRevokedUsers = mockConsoleState.members.filter(
        (member) => member.status !== 'revoked'
      ).length;
      if (mockConsoleState.license && nonRevokedUsers >= mockConsoleState.license.seatLimit) {
        return {
          outcome: 'approval_required',
          request: userRequestSchema.parse({
            id: crypto.randomUUID(),
            email: input.email.toLowerCase(),
            fullName: input.fullName,
            requestedRole: input.role,
            status: 'pending_management_approval',
            createdAt: new Date().toISOString(),
          }),
        };
      }

      return {
        outcome: 'provisioning',
        member: memberSchema.parse({
        id: crypto.randomUUID(),
        email: input.email.toLowerCase(),
        fullName: input.fullName,
        role: input.role,
        status: 'pending_activation',
        createdAt: new Date().toISOString(),
        invitationLastSentAt: new Date().toISOString(),
        invitationCanResendAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        }),
      };
    }

    throw new Error(
      (await readErrorMessage(response)) ?? `User request failed: ${response.status}`
    );
  }

  return z
    .object({
      outcome: z.enum(['provisioning', 'approval_required']),
      member: memberSchema.optional(),
      request: userRequestSchema.optional(),
    })
    .parse(await response.json());
}

export async function validateUserImport(users: UserImportCandidate[]): Promise<UserImportValidation> {
  const response = await fetch('/api/users/import/validate', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ users }),
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) return validateMockUserImport(users);
    throw new Error((await readErrorMessage(response)) ?? `User import validation failed: ${response.status}`);
  }
  return userImportValidationSchema.parse(await response.json());
}

export async function importUsers(users: UserImportInput[]): Promise<Member[]> {
  const response = await fetch('/api/users/import', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ users }),
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const validation = validateMockUserImport(users);
      if (validation.failed) throw new Error('Correct the failed rows before importing users.');
      const now = new Date().toISOString();
      const imported = users.map((user) => memberSchema.parse({
        id: crypto.randomUUID(), ...user, status: 'pending_activation', createdAt: now,
        invitationLastSentAt: now, invitationCanResendAt: new Date(Date.now() + 86_400_000).toISOString(),
        syncState: 'awaiting_sync', observedStatus: null, observedRole: null, observedAt: null,
        invitationState: 'pending', invitationExpiresAt: null, lastLoginAt: null,
      }));
      mockConsoleState.members = [...imported, ...mockConsoleState.members];
      return imported;
    }
    throw new Error((await readErrorMessage(response)) ?? `User import failed: ${response.status}`);
  }
  return z.object({ imported: z.number(), members: z.array(memberSchema) }).parse(await response.json()).members;
}

export async function refreshOrganizationUsers(): Promise<{
  members: Member[];
  imported: number;
  updated: number;
  revoked: number;
  syncedAt: string;
}> {
  const response = await fetch('/api/users/sync', {
    method: 'POST',
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const syncedAt = new Date().toISOString();
      mockConsoleState.members = mockConsoleState.members.map((member) => ({
        ...member,
        observedAt: syncedAt,
      }));
      return {
        members: mockConsoleState.members,
        imported: 0,
        updated: mockConsoleState.members.length,
        revoked: 0,
        syncedAt,
      };
    }
    throw new Error(
      (await readErrorMessage(response)) ?? `Organization user refresh failed: ${response.status}`
    );
  }
  return z.object({
    members: z.array(memberSchema),
    imported: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    revoked: z.number().int().nonnegative(),
    syncedAt: z.string(),
  }).parse(await response.json());
}

function validateMockUserImport(candidates: UserImportCandidate[]): UserImportValidation {
  const existing = new Set(mockConsoleState.members.map((member) => member.email.toLowerCase()));
  const uploaded = new Set<string>();
  const usedSeats = mockConsoleState.members.filter((member) => member.status !== 'revoked').length;
  const availableSeats = Math.max(0, (mockConsoleState.license?.seatLimit ?? 0) - usedSeats);
  let accepted = 0;
  const rows = candidates.map((candidate, index) => {
    const parsed = userImportInputSchema.safeParse(candidate);
    const user = parsed.success ? parsed.data : {
      fullName: candidate.fullName.trim(),
      email: candidate.email.trim(),
      role: candidate.role === 'org_admin' ? 'org_admin' as const : 'member' as const,
    };
    const email = user.email.toLowerCase();
    const errors: string[] = [];
    if (!parsed.success) errors.push(...parsed.error.issues.map((issue) => issue.path[0] === 'role' ? 'Role must be member or org_admin.' : issue.path[0] === 'email' ? 'Enter a valid email address.' : 'Full name is required and must be 120 characters or fewer.'));
    if (uploaded.has(email)) errors.push('Email is duplicated in this file.');
    if (existing.has(email)) errors.push('A user with this email already exists.');
    if (!errors.length && accepted >= availableSeats) errors.push('No licensed seat is available for this user.');
    uploaded.add(email);
    if (!errors.length) accepted += 1;
    return { ...user, email, row: index + 2, valid: errors.length === 0, errors };
  });
  return userImportValidationSchema.parse({
    rows,
    ready: rows.filter((row) => row.valid).length,
    failed: rows.filter((row) => !row.valid).length,
    availableSeats,
  });
}

export async function updateMemberAccess(
  memberId: string,
  input: {
    role?: z.infer<typeof roleSchema>;
    status?: z.infer<typeof userStatusSchema>;
  }
): Promise<Member> {
  const response = await fetch(`/api/users/${memberId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const existing = mockConsoleState.members.find((member) => member.id === memberId);
      if (!existing) throw new Error('Member not found');

      return memberSchema.parse({
        ...existing,
        ...input,
      });
    }

    throw new Error(`User update failed: ${response.status}`);
  }

  return memberSchema.parse(await response.json());
}

export class InvitationReminderError extends Error {
  constructor(
    message: string,
    readonly canResendAt: string | null = null
  ) {
    super(message);
    this.name = 'InvitationReminderError';
  }
}

export async function cancelUserRequest(requestId: string): Promise<void> {
  const response = await fetch(`/api/users/requests/${encodeURIComponent(requestId)}`, {
    method: 'DELETE', credentials: 'include', headers: { accept: 'application/json' },
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      mockConsoleState.userRequests = mockConsoleState.userRequests.filter((item) => item.id !== requestId);
      return;
    }
    throw new Error((await readErrorMessage(response)) ?? `Request cancellation failed: ${response.status}`);
  }
}

export async function markNotificationAsRead(notificationId: string): Promise<void> {
  const response = await fetch(`/api/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'POST', credentials: 'include', headers: { accept: 'application/json' },
  });
  if (!response.ok && !shouldUseLocalMock(response)) {
    throw new Error(`Notification update failed: ${response.status}`);
  }
  const readAt = new Date().toISOString();
  mockConsoleState.notifications = mockConsoleState.notifications.map((item) =>
    item.id === notificationId ? { ...item, readAt } : item
  );
}

export async function markEveryNotificationAsRead(): Promise<void> {
  const response = await fetch('/api/notifications/read-all', {
    method: 'POST', credentials: 'include', headers: { accept: 'application/json' },
  });
  if (!response.ok && !shouldUseLocalMock(response)) {
    throw new Error(`Notification update failed: ${response.status}`);
  }
  const readAt = new Date().toISOString();
  mockConsoleState.notifications = mockConsoleState.notifications.map((item) => ({ ...item, readAt }));
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function downloadLicenseFile(domain: string) {
  const response = await fetch(`/api/license/download?domain=${encodeURIComponent(domain)}`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error((await readErrorMessage(response)) ?? 'License download failed');
  }
  const token = validateLicenseJwt(await response.text(), domain);
  saveBlob(new Blob([token], { type: 'application/jwt' }), 'license.jwt');
}

export function validateLicenseJwt(value: string, expectedDomain: string) {
  const token = value.trim();
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('License download did not return a JWT');
  try {
    const header = JSON.parse(decodeBase64Url(parts[0])) as { alg?: unknown };
    const payload = JSON.parse(decodeBase64Url(parts[1])) as { domain?: unknown; exp?: unknown };
    if (header.alg !== 'ES256') throw new Error('License JWT must use ES256');
    if (typeof payload.domain !== 'string' || !licenseDomainMatches(expectedDomain, payload.domain)) {
      throw new Error(`License JWT is not valid for ${expectedDomain}`);
    }
    if (typeof payload.exp === 'number' && Date.now() > payload.exp * 1000) {
      throw new Error('License JWT has expired');
    }
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('License JWT')) throw error;
    throw new Error('License download returned an invalid JWT');
  }
  return token;
}

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
  return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
}

function licenseDomainMatches(requested: string, licensed: string) {
  return requested === licensed || (requested === 'localhost' && licensed === '127.0.0.1');
}

export async function downloadReleaseFile(release: SoftwareRelease) {
  if (!release.downloadUrl) throw new Error('This release has no downloadable artifact');
  const response = await fetch(release.downloadUrl, { credentials: 'include' });
  if (!response.ok) {
    throw new Error((await readErrorMessage(response)) ?? 'Release download failed');
  }
  saveBlob(await response.blob(), release.fileName);
}

async function loadLocalMockConsoleState(): Promise<ConsoleState> {
  try {
    const response = await fetch('/api/dev/releases/latest', { headers: { accept: 'application/json' } });
    if (!response.ok) return { ...mockConsoleState, releases: [] };
    const release = softwareReleaseSchema.parse(await response.json());
    return { ...mockConsoleState, releases: [release] };
  } catch {
    return { ...mockConsoleState, releases: [] };
  }
}

export async function resendMemberInvitation(memberId: string): Promise<{
  memberId: string;
  invitationLastSentAt: string;
  invitationCanResendAt: string;
}> {
  const response = await fetch(
    `/api/users/${encodeURIComponent(memberId)}/invitation/resend`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json' },
    }
  );

  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const member = mockConsoleState.members.find((candidate) => candidate.id === memberId);
      if (!member) throw new InvitationReminderError('Member not found');
      if (member.status !== 'pending_activation') {
        throw new InvitationReminderError('Only pending members can receive an invitation reminder');
      }

      if (
        member.invitationCanResendAt &&
        Date.parse(member.invitationCanResendAt) > Date.now()
      ) {
        throw new InvitationReminderError(
          'This invitation can only be resent once every 24 hours',
          member.invitationCanResendAt
        );
      }

      const invitationLastSentAt = new Date().toISOString();
      const invitationCanResendAt = new Date(
        Date.now() + 24 * 60 * 60 * 1000
      ).toISOString();
      mockConsoleState.members = mockConsoleState.members.map((candidate) =>
        candidate.id === memberId
          ? { ...candidate, invitationLastSentAt, invitationCanResendAt }
          : candidate
      );
      return { memberId, invitationLastSentAt, invitationCanResendAt };
    }

    const body = await response
      .json()
      .catch(() => null) as { error?: unknown; canResendAt?: unknown } | null;
    throw new InvitationReminderError(
      typeof body?.error === 'string'
        ? body.error
        : `Invitation resend failed: ${response.status}`,
      typeof body?.canResendAt === 'string' ? body.canResendAt : null
    );
  }

  return z
    .object({
      memberId: z.string(),
      invitationLastSentAt: z.string(),
      invitationCanResendAt: z.string(),
    })
    .parse(await response.json());
}

export async function revokeMemberInvitation(memberId: string): Promise<Member> {
  const response = await fetch(`/api/users/${encodeURIComponent(memberId)}/invitation/revoke`, {
    method: 'POST',
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const member = mockConsoleState.members.find((candidate) => candidate.id === memberId);
      if (!member) throw new Error('Member not found');
      const updated = memberSchema.parse({
        ...member,
        status: 'revoked',
        syncState: 'awaiting_sync',
        invitationState: 'revoked',
        invitationExpiresAt: null,
      });
      mockConsoleState.members = mockConsoleState.members.map((candidate) => candidate.id === memberId ? updated : candidate);
      return updated;
    }
    throw new Error((await readErrorMessage(response)) ?? `Invitation revocation failed: ${response.status}`);
  }
  return memberSchema.parse(await response.json());
}

export async function retryPlatformMemberSync(organizationId: string, memberId: string): Promise<void> {
  const response = await fetch(
    `/api/platform/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(memberId)}/sync/retry`,
    { method: 'POST', credentials: 'include', headers: { accept: 'application/json' } }
  );
  if (!response.ok && !shouldUseLocalMock(response)) {
    throw new Error((await readErrorMessage(response)) ?? `WorkOS retry failed: ${response.status}`);
  }
}

export async function resendPlatformMemberInvitation(organizationId: string, memberId: string): Promise<void> {
  const response = await fetch(
    `/api/platform/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(memberId)}/invitation/resend`,
    { method: 'POST', credentials: 'include', headers: { accept: 'application/json' } }
  );
  if (!response.ok && !shouldUseLocalMock(response)) {
    throw new Error((await readErrorMessage(response)) ?? `Invitation resend failed: ${response.status}`);
  }
}

export async function revokePlatformMemberInvitation(organizationId: string, memberId: string): Promise<void> {
  const response = await fetch(
    `/api/platform/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(memberId)}/invitation/revoke`,
    { method: 'POST', credentials: 'include', headers: { accept: 'application/json' } }
  );
  if (!response.ok && !shouldUseLocalMock(response)) {
    throw new Error((await readErrorMessage(response)) ?? `Invitation revocation failed: ${response.status}`);
  }
}

export async function createTicket(input: {
  subject: string;
  priority: 'low' | 'normal' | 'high';
  message: string;
}): Promise<Ticket> {
  const response = await fetch('/api/tickets', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      const ticket = ticketSchema.parse({
        id: crypto.randomUUID(),
        organizationId: mockConsoleState.organization.id,
        organizationName: mockConsoleState.organization.name,
        subject: input.subject,
        status: 'open',
        priority: input.priority,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: [
          {
            id: crypto.randomUUID(),
            authorType: 'customer',
            authorName: mockConsoleState.currentUser.fullName,
            authorEmail: mockConsoleState.currentUser.email,
            body: input.message,
            createdAt: new Date().toISOString(),
            attachments: [],
          },
        ],
      });
      mockConsoleState.tickets.unshift(ticket);
      return ticket;
    }

    throw new Error(
      (await readErrorMessage(response)) ?? `Ticket request failed: ${response.status}`
    );
  }

  return ticketSchema.parse(await response.json());
}

export async function uploadTicketAttachment(ticketId: string, file: File): Promise<TicketAttachment> {
  return uploadAttachment(`/api/tickets/${encodeURIComponent(ticketId)}/attachments`, file, true);
}

export async function deleteTicketAttachment(ticketId: string, attachmentId: string): Promise<void> {
  const response = await fetch(
    `/api/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: 'DELETE', credentials: 'include' }
  );
  if (!response.ok && !shouldUseLocalMock(response)) {
    throw new Error(`Attachment delete failed: ${response.status}`);
  }
}

export async function replyToTicket(
  ticketId: string,
  message: string,
  attachments: TicketAttachment[] = []
): Promise<Ticket> {
  return ticketRequest(
    `/api/tickets/${ticketId}/messages`,
    'POST',
    { message, attachmentIds: attachments.map((attachment) => attachment.id) },
    attachments
  );
}

export async function updateTicketStatus(ticketId: string, status: 'open' | 'in_progress' | 'closed'): Promise<Ticket> {
  return ticketRequest(`/api/tickets/${ticketId}`, 'PATCH', { status });
}

export async function replyToPlatformTicket(
  organizationId: string,
  ticketId: string,
  message: string,
  attachments: TicketAttachment[] = []
): Promise<Ticket> {
  return ticketRequest(
    `/api/platform/organizations/${organizationId}/tickets/${ticketId}/messages`,
    'POST',
    { message, attachmentIds: attachments.map((attachment) => attachment.id) },
    attachments
  );
}

export async function uploadPlatformTicketAttachment(
  organizationId: string,
  ticketId: string,
  file: File
): Promise<TicketAttachment> {
  return uploadAttachment(
    `/api/platform/organizations/${encodeURIComponent(organizationId)}/tickets/${encodeURIComponent(ticketId)}/attachments`,
    file,
    true
  );
}

export async function deletePlatformTicketAttachment(
  organizationId: string,
  ticketId: string,
  attachmentId: string
): Promise<void> {
  const response = await fetch(
    `/api/platform/organizations/${encodeURIComponent(organizationId)}/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: 'DELETE', credentials: 'include' }
  );
  if (!response.ok && !shouldUseLocalMock(response)) {
    throw new Error(`Attachment delete failed: ${response.status}`);
  }
}

export async function updatePlatformTicketStatus(
  organizationId: string,
  ticketId: string,
  status: 'open' | 'in_progress' | 'closed'
): Promise<Ticket> {
  return ticketRequest(`/api/platform/organizations/${organizationId}/tickets/${ticketId}`, 'PATCH', {
    status,
  });
}

export async function decidePlatformUserRequest(
  organizationId: string,
  requestId: string,
  decision: 'approve' | 'reject',
  note = ''
) {
  const path = `/api/platform/organizations/${encodeURIComponent(organizationId)}/user-requests/${encodeURIComponent(requestId)}/decision`;
  const response = await fetch(path, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify({ decision, note }) });
  if (!response.ok) {
    if (shouldUseLocalMock(response)) {
      mockPlatformState = { ...mockPlatformState, organizations: mockPlatformState.organizations.map((organization) => organization.id === organizationId ? { ...organization, userRequests: organization.userRequests.filter((item) => item.id !== requestId) } : organization) };
      return;
    }
    throw new Error((await readErrorMessage(response)) ?? `Approval decision failed: ${response.status}`);
  }
}

export async function uploadAndPublishPlatformReleaseLegacy(input: {
  version: string;
  channel: 'stable' | 'beta';
  releaseNotes: string;
  file: File;
}) {
  const digest = await crypto.subtle.digest('SHA-256', await input.file.arrayBuffer());
  const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  const created = await postJson('/api/platform/releases', {
    version: input.version,
    channel: input.channel,
    releaseNotes: input.releaseNotes,
    fileName: input.file.name,
    contentType: input.file.type || 'application/octet-stream',
    sizeBytes: input.file.size,
    sha256,
  }) as { id: string; artifactUploadUrl: string };
  const upload = await fetch(created.artifactUploadUrl, {
    method: 'PUT', credentials: 'include',
    headers: { 'content-type': input.file.type || 'application/octet-stream' },
    body: input.file,
  });
  if (!upload.ok) throw new Error((await readErrorMessage(upload)) ?? `Artifact upload failed: ${upload.status}`);
  await postJson(`/api/platform/releases/${encodeURIComponent(created.id)}/publish`, {});
}

async function releaseFileSha256(file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function uploadPlatformBuild(input: {
  version: string;
  deploymentModel: 'saas_hybrid' | 'on_prem';
  buildProfile: 'workos' | 'license';
  cloudflareProject: 'lunex-mypdf' | '8m48e-mypdf';
  file: File;
}) {
  const sha256 = await releaseFileSha256(input.file);
  const response = await fetch('/api/platform/builds', {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({
      version: input.version,
      deploymentModel: input.deploymentModel,
      buildProfile: input.buildProfile,
      cloudflareProject: input.cloudflareProject,
      fileName: input.file.name,
      contentType: input.file.type || 'application/octet-stream',
      sizeBytes: input.file.size,
      sha256,
    }),
  });
  if (!response.ok && shouldUseLocalMock(response)) {
    const now = new Date().toISOString();
    const release = softwareReleaseSchema.parse({
      id: crypto.randomUUID(), version: input.version, channel: 'stable', releaseNotes: '', fileName: input.file.name,
      deploymentModel: input.deploymentModel, buildProfile: input.buildProfile, sourceCommit: null, sourceRef: null,
      cloudflareProject: input.cloudflareProject, cloudflareDeploymentId: null,
      contentType: input.file.type || 'application/octet-stream', sizeBytes: input.file.size, sha256, status: 'draft',
      publishedAt: null, classifiedAt: null, artifactUploadedAt: now, audience: 'all', targetCount: 0, downloadUrl: null,
    });
    mockPlatformState = { ...mockPlatformState, releases: [release, ...mockPlatformState.releases] };
    return release;
  }
  if (!response.ok) throw new Error((await readErrorMessage(response)) ?? `Build intake failed: ${response.status}`);
  const created = await response.json() as { id: string; artifactUploadUrl: string };
  const upload = await fetch(created.artifactUploadUrl, {
    method: 'PUT', credentials: 'include', headers: { 'content-type': input.file.type || 'application/octet-stream' }, body: input.file,
  });
  if (!upload.ok) throw new Error((await readErrorMessage(upload)) ?? `Artifact upload failed: ${upload.status}`);
  return created;
}

export async function classifyPlatformBuild(releaseId: string, input: { channel: 'stable' | 'beta'; releaseNotes: string }) {
  const response = await fetch(`/api/platform/releases/${encodeURIComponent(releaseId)}/classification`, {
    method: 'PATCH', credentials: 'include', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(input),
  });
  if (!response.ok && shouldUseLocalMock(response)) {
    const now = new Date().toISOString();
    mockPlatformState = { ...mockPlatformState, releases: mockPlatformState.releases.map((release) => release.id === releaseId ? { ...release, ...input, classifiedAt: now } : release) };
    return;
  }
  if (!response.ok) throw new Error((await readErrorMessage(response)) ?? `Build classification failed: ${response.status}`);
}

export async function publishPlatformRelease(releaseId: string, input: { audience: 'all' | 'selected'; targetOrganizationIds: string[] }) {
  const response = await fetch(`/api/platform/releases/${encodeURIComponent(releaseId)}/publish`, {
    method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(input),
  });
  if (!response.ok && shouldUseLocalMock(response)) {
    const now = new Date().toISOString();
    mockPlatformState = { ...mockPlatformState, releases: mockPlatformState.releases.map((release) => release.id === releaseId ? { ...release, status: 'published' as const, publishedAt: now, audience: input.audience, targetCount: input.audience === 'all' ? mockPlatformState.organizations.length : input.targetOrganizationIds.length, downloadUrl: `/api/updates/releases/${releaseId}/download` } : release) };
    return;
  }
  if (!response.ok) throw new Error((await readErrorMessage(response)) ?? `Release publication failed: ${response.status}`);
}

export function loginUrl() {
  return '/api/auth/login';
}

export function logoutUrl() {
  return '/api/auth/logout';
}

export async function createPlatformOrganization(input: {
  name: string;
  slug: string;
  workosOrganizationId?: string | null;
  domains: Array<{ hostname: string; environment: 'local' | 'staging' | 'production'; active: boolean }>;
  license?: {
    status: 'active' | 'expiring' | 'expired' | 'suspended';
    startDate?: string | null;
    expiryDate: string;
    seatLimit: number;
    limitsSummary: string;
    licensedDomains: string[];
    releaseChannel?: 'stable' | 'beta';
    deploymentModel?: 'saas_hybrid' | 'on_prem';
  };
}) {
  return postJson('/api/platform/organizations', input);
}

export async function upsertPlatformLicense(
  organizationId: string,
  input: {
    status: 'active' | 'expiring' | 'expired' | 'suspended';
    startDate?: string | null;
    expiryDate: string;
    seatLimit: number;
    limitsSummary: string;
    licensedDomains: string[];
    releaseChannel?: 'stable' | 'beta';
    deploymentModel?: 'saas_hybrid' | 'on_prem';
  }
) {
  return putJson(`/api/platform/organizations/${organizationId}/license`, input);
}

export async function createPlatformDomain(
  organizationId: string,
  input: { hostname: string; environment: 'local' | 'staging' | 'production'; active: boolean }
) {
  return postJson(`/api/platform/organizations/${organizationId}/domains`, input);
}

export async function updatePlatformDomain(
  organizationId: string,
  domainId: string,
  input: Partial<{ hostname: string; environment: 'local' | 'staging' | 'production'; active: boolean }>
) {
  return patchJson(`/api/platform/organizations/${organizationId}/domains/${domainId}`, input);
}

export async function createPlatformMember(
  organizationId: string,
  input: {
    email: string;
    fullName: string;
    role: z.infer<typeof roleSchema>;
    status: z.infer<typeof userStatusSchema>;
  }
): Promise<{ id: string }> {
  return z.object({ id: z.string() }).parse(await postJson(`/api/platform/organizations/${organizationId}/members`, input));
}

export async function updatePlatformMember(
  organizationId: string,
  memberId: string,
  input: {
    fullName?: string;
    role?: z.infer<typeof roleSchema>;
    status?: z.infer<typeof userStatusSchema>;
  }
) {
  return patchJson(`/api/platform/organizations/${organizationId}/members/${memberId}`, input);
}

async function postJson(path: string, input: unknown): Promise<unknown> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json() as Promise<unknown>;
}

async function putJson(path: string, input: unknown): Promise<unknown> {
  const response = await fetch(path, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json() as Promise<unknown>;
}

async function patchJson(path: string, input: unknown): Promise<unknown> {
  const response = await fetch(path, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json() as Promise<unknown>;
}

async function ticketRequest(
  path: string,
  method: 'POST' | 'PATCH',
  input: unknown,
  localAttachments: TicketAttachment[] = []
): Promise<Ticket> {
  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    if (shouldUseLocalMock(response) && path.startsWith('/api/platform/organizations/')) {
      const segments = path.split('/');
      const organizationId = decodeURIComponent(segments[4] ?? '');
      const ticketId = decodeURIComponent(segments[6] ?? '');
      const organization = mockPlatformState.organizations.find((item) => item.id === organizationId);
      const existing = organization?.tickets.find((ticket) => ticket.id === ticketId);
      if (!organization || !existing) throw new Error('Ticket not found');

      const now = new Date().toISOString();
      let updated = existing;
      if (path.endsWith('/messages') && typeof input === 'object' && input && 'message' in input) {
        updated = ticketSchema.parse({
          ...existing,
          status: existing.status === 'open' ? 'in_progress' : existing.status,
          updatedAt: now,
          messages: [
            ...existing.messages,
            {
              id: crypto.randomUUID(),
              authorType: 'platform',
              authorName: mockPlatformState.currentUser.fullName,
              authorEmail: mockPlatformState.currentUser.email,
              body: String(input.message),
              createdAt: now,
              attachments: localAttachments,
            },
          ],
        });
      } else if (typeof input === 'object' && input && 'status' in input) {
        updated = ticketSchema.parse({
          ...existing,
          status: String(input.status),
          updatedAt: now,
        });
      }

      mockPlatformState = {
        ...mockPlatformState,
        organizations: mockPlatformState.organizations.map((item) =>
          item.id === organizationId
            ? {
                ...item,
                tickets: item.tickets.map((ticket) => (ticket.id === ticketId ? updated : ticket)),
              }
            : item
        ),
      };
      return updated;
    }

    if (shouldUseLocalMock(response) && path.startsWith('/api/tickets/')) {
      const ticketId = path.split('/')[3];
      const existing = mockConsoleState.tickets.find((ticket) => ticket.id === ticketId);
      if (!existing) throw new Error('Ticket not found');
      const now = new Date().toISOString();
      if (path.endsWith('/messages') && typeof input === 'object' && input && 'message' in input) {
        const ticket = ticketSchema.parse({
          ...existing,
          status: 'open',
          updatedAt: now,
          messages: [
            ...existing.messages,
            {
              id: crypto.randomUUID(),
              authorType: 'customer',
              authorName: mockConsoleState.currentUser.fullName,
              authorEmail: mockConsoleState.currentUser.email,
              body: String(input.message),
              createdAt: now,
              attachments: localAttachments,
            },
          ],
        });
        replaceMockTicket(ticket);
        return ticket;
      }

      if (typeof input === 'object' && input && 'status' in input) {
        const ticket = ticketSchema.parse({
          ...existing,
          status: String(input.status),
          updatedAt: now,
        });
        replaceMockTicket(ticket);
        return ticket;
      }
    }

    throw new Error(
      (await readErrorMessage(response)) ?? `Ticket request failed: ${response.status}`
    );
  }
  const ticket = ticketSchema.parse(await response.json());
  replaceMockTicket(ticket);
  return ticket;
}

function replaceMockTicket(ticket: Ticket) {
  const index = mockConsoleState.tickets.findIndex((item) => item.id === ticket.id);
  if (index >= 0) mockConsoleState.tickets[index] = ticket;
}

async function uploadAttachment(
  path: string,
  file: File,
  allowLocalMock: boolean
): Promise<TicketAttachment> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-file-size': String(file.size),
    },
    body: file,
  });

  if (!response.ok) {
    if (allowLocalMock && shouldUseLocalMock(response)) {
      return ticketAttachmentSchema.parse({
        id: crypto.randomUUID(),
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        kind: file.type.startsWith('image/') ? 'image' : 'document',
        url: await fileToDataUrl(file),
      });
    }
    throw new Error((await readErrorMessage(response)) ?? `Attachment upload failed: ${response.status}`);
  }

  return ticketAttachmentSchema.parse(await response.json());
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Unable to read attachment'));
    });
    reader.addEventListener('error', () => reject(reader.error ?? new Error('Unable to read attachment')));
    reader.readAsDataURL(file);
  });
}

const localDevelopmentHosts = new Set([
  'localhost',
  '127.0.0.1',
  '192.168.100.247',
  '100.106.137.33',
]);

function isLocalDevelopmentHost() {
  return typeof window !== 'undefined' && localDevelopmentHosts.has(window.location.hostname);
}

function shouldUseLocalMock(response: Response) {
  return (
    import.meta.env.DEV &&
    response.status === 404 &&
    isLocalDevelopmentHost()
  );
}

function shouldUseVitePlatformMock() {
  return (
    import.meta.env.DEV &&
    import.meta.env.VITE_LOCAL_CONSOLE_MODE === 'platform' &&
    isLocalDevelopmentHost()
  );
}
